import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
    AlertTriangle,
    Award,
    BarChart3,
    CheckCircle2,
    Clock3,
    Filter,
    Gauge,
    RefreshCcw,
    TrendingUp,
    Users,
    X,
} from "lucide-react";
import {
    Bar,
    BarChart,
    CartesianGrid,
    Cell,
    Pie,
    PieChart,
    ResponsiveContainer,
    Tooltip as RechartsTooltip,
    XAxis,
    YAxis,
} from "recharts";

import { fetchCampagnesRequest } from "../../../redux/actions/campagnesActions";
import { fetchManagerInboxRequest } from "../../../redux/actions/skillEvaluationActions";
import { getDirectReportsRequest } from "../../../redux/actions/userActions";
import "../rf/DashboardSkillsRespo.css";

const COLORS = {
    primary: "#204385",
    secondary: "#ee645f",
    success: "#4caf50",
    warning: "#ff9800",
    error: "#f44336",
    info: "#2196f3",
    grey: "#757575",
};

const CHART_COLORS = [COLORS.primary, COLORS.secondary, COLORS.success, COLORS.warning, COLORS.info, "#9c27b0"];
const DOMAIN_SCORE_COLORS = [COLORS.primary, COLORS.secondary];
const REPARTITION_COLORS = ["#4f7df3", "#48b887", COLORS.secondary, COLORS.primary, COLORS.warning, COLORS.info];

const STATUS_META = {
    NOT_STARTED: { label: "Non demarrée", color: COLORS.secondary },
    EN_ATTENTE_COLLAB: { label: "Attente collab.", color: COLORS.warning },
    EN_ATTENTE_MANAGER: { label: "À traiter", color: COLORS.secondary },
    EN_ATTENTE_N2: { label: "Attente N+2", color: COLORS.info },
    VALIDEE: { label: "Validée", color: COLORS.primary },
    REJETEE: { label: "Rejetée", color: COLORS.error },
};

const numberOrNull = (value) => {
    const n = Number(value);
    return Number.isFinite(n) ? n : null;
};

const round1 = (value) => Math.round(value * 10) / 10;
const pct = (value, total) => (!total ? 0 : Math.round((value / total) * 100));
const statusCode = (statut) => String(typeof statut === "string" ? statut : statut?.name || "").toUpperCase();

const average = (values) => {
    const nums = (values || []).map(numberOrNull).filter((n) => n !== null);
    if (!nums.length) return null;
    return round1(nums.reduce((sum, n) => sum + n, 0) / nums.length);
};

const getSkillScore = (niveau) =>
    numberOrNull(niveau?.scoreObtenu) ??
    numberOrNull(niveau?.scoreN2) ??
    numberOrNull(niveau?.scoreManager) ??
    numberOrNull(niveau?.scoreCollab);

const getExpectedScore = (niveau) =>
    numberOrNull(niveau?.scoreRequis) ??
    numberOrNull(niveau?.niveauRequis) ??
    numberOrNull(niveau?.niveauAttendu) ??
    numberOrNull(niveau?.scoreAttendu);

const getUserName = (evaluation) =>
    evaluation?.userFullName ||
    evaluation?.fullName ||
    evaluation?.nom ||
    `Collaborateur ${evaluation?.userId || evaluation?.id || ""}`.trim();

function SectionTitle({ icon, children }) {
    return (
        <div className="dsrSectionTitle">
            <span className="dsrSectionTitle__icon">{icon}</span>
            <span>{children}</span>
        </div>
    );
}

function StatCard({ title, value, subtitle, icon, color, onClick }) {
    return (
        <div
            className={`dsrStatCard${onClick ? " dsrStatCard--clickable" : ""}`}
            onClick={onClick}
            role={onClick ? "button" : undefined}
            tabIndex={onClick ? 0 : undefined}
            onKeyDown={(event) => {
                if (onClick && (event.key === "Enter" || event.key === " ")) onClick();
            }}
        >
            <div className="dsrStatCard__row">
                <div>
                    <div className="dsrStatCard__title">{title}</div>
                    <div className="dsrStatCard__value" style={{ color }}>{value}</div>
                    {subtitle ? <div className="dsrStatCard__sub">{subtitle}</div> : null}
                </div>
                <div className="dsrStatIcon" style={{ color, background: `${color}15`, borderColor: `${color}25` }}>
                    {icon}
                </div>
            </div>
        </div>
    );
}

function DetailsModal({ title, subtitle, children, onClose }) {
    return (
        <div className="dsrModalBackdrop" onMouseDown={(event) => event.target === event.currentTarget && onClose()}>
            <div className="dsrModalPanel">
                <div className="dsrModalHeader">
                    <div>
                        <div className="dsrModalTitle">{title}</div>
                        {subtitle ? <div className="dsrModalSubtitle">{subtitle}</div> : null}
                    </div>
                    <button className="dsrModalClose" type="button" onClick={onClose} title="Fermer">
                        <X size={18} />
                    </button>
                </div>
                <div className="dsrModalBody">{children}</div>
            </div>
        </div>
    );
}

function ChartCard({ title, children }) {
    return (
        <div className="dsrChartCard">
            <div className="dsrChartCard__title">{title}</div>
            <div className="dsrChartCard__body">{children}</div>
        </div>
    );
}

export default function DashboardSkillsManager() {
    const dispatch = useDispatch();
    const { user } = useSelector((state) => state.auth || {});
    const { list: campagnesRaw } = useSelector((state) => state.campagnes || { list: [] });
    const { inboxManager = [], loading } = useSelector((state) => state.skillEvaluation || {});
    const { directReports = [] } = useSelector((state) => state.user || {});

    const [campagneId, setCampagneId] = useState("");
    const [detailsModal, setDetailsModal] = useState(null);

    useEffect(() => {
        dispatch(fetchCampagnesRequest());
        dispatch(fetchManagerInboxRequest());
        if (user?.id || user?.uid) dispatch(getDirectReportsRequest(user.id || user.uid));
    }, [dispatch, user?.id, user?.uid]);

    const campagnes = useMemo(() => {
        return (campagnesRaw || [])
            .filter((c) => String(c?.typeCampagne || "").toUpperCase() === "SKILLS" || !c?.typeCampagne)
            .sort((a, b) => (a?.status === b?.status ? 0 : a?.status === true ? -1 : 1));
    }, [campagnesRaw]);

    useEffect(() => {
        if (!campagnes.length) {
            setCampagneId("");
            return;
        }
        if (!campagneId || !campagnes.some((campagne) => String(campagne.id) === String(campagneId))) {
            const defaultCampagne = campagnes.find((campagne) => campagne?.isDefault) || campagnes.find((campagne) => campagne?.status === true) || campagnes[0];
            setCampagneId(String(defaultCampagne?.id || ""));
        }
    }, [campagnes, campagneId]);

    const refreshData = useCallback(() => {
        dispatch(fetchManagerInboxRequest());
    }, [dispatch]);

    const teamRows = useMemo(() => {
        const evaluations = (inboxManager || []).filter((evaluation) => !campagneId || Number(evaluation.campagneId) === Number(campagneId));
        const reportsById = new Map((directReports || []).map((report) => [report.id, report]));

        const reportRows = (directReports || []).map((report) => {
            const evaluation = evaluations.find((item) => Number(item.userId) === Number(report.id));
            return {
                ...evaluation,
                userId: report.id,
                userFullName: report.fullName || evaluation?.userFullName,
                jobName: report.jobName || evaluation?.jobName,
                statut: evaluation?.statut || "NOT_STARTED",
                niveaux: evaluation?.niveaux || [],
                hasEvaluation: !!evaluation,
            };
        });

        const extraRows = evaluations
            .filter((evaluation) => !reportsById.has(evaluation.userId))
            .map((evaluation) => ({ ...evaluation, hasEvaluation: true }));

        return reportRows.length ? [...reportRows, ...extraRows] : evaluations;
    }, [inboxManager, directReports, campagneId]);

    const analytics = useMemo(() => {
        const totalTeam = Math.max(teamRows.length, directReports.length);
        const evaluations = teamRows.filter((row) => row.hasEvaluation !== false);
        const statusCounts = teamRows.reduce((acc, row) => {
            const code = statusCode(row.statut) || "NOT_STARTED";
            acc[code] = (acc[code] || 0) + 1;
            return acc;
        }, {});

        const allLevels = evaluations.flatMap((evaluation) =>
            (evaluation.niveaux || []).map((niveau) => ({ ...niveau, evaluation }))
        );
        const scoredLevels = allLevels
            .map((item) => ({ ...item, score: getSkillScore(item), expected: getExpectedScore(item) }))
            .filter((item) => item.score !== null);
        const gaps = scoredLevels
            .filter((item) => item.expected !== null)
            .map((item) => ({ ...item, gap: item.expected - item.score }));

        const avgScore = average(scoredLevels.map((item) => item.score));
        const avgGap = average(gaps.map((item) => item.gap));
        const criticalGaps = gaps.filter((item) => item.gap >= 1).length;
        const teamDetails = teamRows
            .map((row) => {
                const levels = (row.niveaux || [])
                    .map((niveau) => ({ score: getSkillScore(niveau), expected: getExpectedScore(niveau) }))
                    .filter((niveau) => niveau.score !== null);
                const rowGaps = levels.filter((niveau) => niveau.expected !== null).map((niveau) => niveau.expected - niveau.score);
                return {
                    collaborator: getUserName(row),
                    job: row.jobName || row.poste || row.userEmployeeCode || "-",
                    status: statusCode(row.statut) || "NOT_STARTED",
                    score: average(levels.map((niveau) => niveau.score)),
                    gap: average(rowGaps),
                    skillsCount: row.niveaux?.length || 0,
                    hasEvaluation: row.hasEvaluation !== false,
                };
            })
            .sort((a, b) => a.collaborator.localeCompare(b.collaborator));
        const toReviewDetails = teamDetails.filter((row) => row.status === "EN_ATTENTE_MANAGER");
        const criticalGapDetails = gaps
            .filter((item) => item.gap >= 1)
            .map((item) => ({
                collaborator: getUserName(item.evaluation),
                job: item.evaluation?.jobName || item.evaluation?.poste || item.evaluation?.userEmployeeCode || "-",
                skill: item.skillNom || item.skillCode || "Compétence",
                domain: item.domaineNom || item.domaineName || "-",
                expected: item.expected,
                score: item.score,
                gap: item.gap,
                status: statusCode(item.evaluation?.statut),
            }))
            .sort((a, b) => b.gap - a.gap || a.collaborator.localeCompare(b.collaborator));

        const statusData = Object.entries(statusCounts).map(([code, value]) => ({
            name: STATUS_META[code]?.label || code,
            value,
            color: STATUS_META[code]?.color || COLORS.grey,
        }));

        const collaboratorScores = teamRows.map((row) => {
            const levels = (row.niveaux || [])
                .map((niveau) => ({ score: getSkillScore(niveau), expected: getExpectedScore(niveau) }))
                .filter((niveau) => niveau.score !== null);
            const rowGaps = levels.filter((niveau) => niveau.expected !== null).map((niveau) => niveau.expected - niveau.score);
            return {
                name: getUserName(row),
                score: average(levels.map((niveau) => niveau.score)) || 0,
                gap: average(rowGaps) || 0,
                status: statusCode(row.statut),
            };
        });

        const byDomain = Object.values(
            scoredLevels.reduce((acc, item) => {
                const name = item.domaineNom || item.domaineName || "Non classe";
                if (!acc[name]) acc[name] = { name, scores: [], gaps: [] };
                acc[name].scores.push(item.score);
                if (item.expected !== null) acc[name].gaps.push(item.expected - item.score);
                return acc;
            }, {})
        )
            .map((item) => ({ name: item.name, score: average(item.scores) || 0, gap: average(item.gaps) || 0 }))
            .sort((a, b) => b.gap - a.gap)
            .slice(0, 8);

        const skills = Object.values(
            scoredLevels.reduce((acc, item) => {
                const key = item.skillId || item.skillNom || item.skillCode;
                const name = item.skillNom || item.skillCode || "Compétence";
                if (!acc[key]) acc[key] = { name, scores: [], gaps: [], count: 0 };
                acc[key].scores.push(item.score);
                if (item.expected !== null) acc[key].gaps.push(item.expected - item.score);
                acc[key].count += 1;
                return acc;
            }, {})
        ).map((item) => ({ name: item.name, score: average(item.scores) || 0, gap: average(item.gaps) || 0, count: item.count }));

        return {
            totalTeam,
            totalEvaluations: evaluations.length,
            submitted: Math.max(totalTeam - (statusCounts.NOT_STARTED || 0) - (statusCounts.EN_ATTENTE_COLLAB || 0), 0),
            toReview: statusCounts.EN_ATTENTE_MANAGER || 0,
            waitingN2: statusCounts.EN_ATTENTE_N2 || 0,
            validated: statusCounts.VALIDEE || 0,
            completionRate: pct(statusCounts.VALIDEE || 0, totalTeam),
            avgScore,
            avgGap,
            criticalGaps,
            teamDetails,
            toReviewDetails,
            criticalGapDetails,
            statusData,
            collaboratorScores,
            byDomain,
            criticalSkills: [...skills].filter((skill) => skill.gap > 0).sort((a, b) => b.gap - a.gap).slice(0, 5),
            strongSkills: [...skills].sort((a, b) => b.score - a.score).slice(0, 5),
        };
    }, [teamRows, directReports.length]);

    return (
        <div className="dsrPage">
            <header className="dsrHeader">
                <div>
                    <h1>Tableau de bord de mon équipe</h1>
                </div>
                <div className="dsrActions">
                    <div className="dsrSelectWrap">
                        <Filter size={15}/>
                        <label htmlFor="dsm-campagne">Campagne</label>
                        <select id="dsm-campagne" className="dsrSelect" value={campagneId}
                                onChange={(event) => setCampagneId(event.target.value)}>
                            <option value="">Toutes les campagnes</option>
                            {campagnes.map((campagne) => (
                                <option key={campagne.id}
                                        value={campagne.id}>{campagne.nom || campagne.name || `Campagne ${campagne.id}`}</option>
                            ))}
                        </select>
                    </div>
                </div>
            </header>

            <main className="dsrMain">
                <SectionTitle icon={<Gauge size={18} />}>Pilotage de l'équipe</SectionTitle>
                <div className="dsrGrid dsrGrid--4">
                    <StatCard title="Collaborateurs" value={analytics.totalTeam} icon={<Users size={20} />} color={COLORS.primary} onClick={() => setDetailsModal("team")} />
                    <StatCard title="À traiter" value={analytics.toReview}  icon={<Clock3 size={20} />} color={COLORS.secondary} onClick={() => setDetailsModal("to-review")} />
                    <StatCard title="Écarts critiques" value={analytics.criticalGaps} icon={<AlertTriangle size={20} />} color={COLORS.error} onClick={() => setDetailsModal("critical-gaps")} />
                    <StatCard title="Score moyen de l'équipe" value={analytics.avgScore ?? "-"} icon={<Award size={20} />} color={COLORS.info} />
                </div>

                <SectionTitle icon={<BarChart3 size={18} />}>Analyse de l'équipe</SectionTitle>
                <div className="dsrGrid dsrGrid--2">
                    <ChartCard title="Répartition des statuts">
                        <ResponsiveContainer width="100%" height={300}>
                            <PieChart>
                                <Pie data={analytics.statusData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={92} label={({ name, value }) => `${name}: ${value}`}>
                                    {analytics.statusData.map((entry, index) => <Cell key={entry.name} fill={REPARTITION_COLORS[index % REPARTITION_COLORS.length]} />)}
                                </Pie>
                                <RechartsTooltip />
                            </PieChart>
                        </ResponsiveContainer>
                    </ChartCard>

                    <ChartCard title="Score moyen par domaine">
                        <ResponsiveContainer width="100%" height={320}>
                            <BarChart data={analytics.byDomain}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                                <XAxis dataKey="name" interval={0} tick={{ fontSize: 11 }} />
                                <YAxis domain={[0, 4]} />
                                <RechartsTooltip />
                                <Bar dataKey="score" radius={[8, 8, 0, 0]}>
                                    {analytics.byDomain.map((entry, index) => (
                                        <Cell key={entry.name} fill={DOMAIN_SCORE_COLORS[index % DOMAIN_SCORE_COLORS.length]} />
                                    ))}
                                </Bar>
                            </BarChart>
                        </ResponsiveContainer>
                    </ChartCard>
                </div>

                <SectionTitle icon={<AlertTriangle size={18} />}>Actions prioritaires</SectionTitle>
                <div className="dsrGrid dsrGrid--2">
                    <div className="dsrPanel">
                        <div className="dsrPanel__title">Compétences à renforcer</div>
                        <div className="dsrPanel__body">
                            {analytics.criticalSkills.length ? (
                                <div className="dsrRiskList">
                                    {analytics.criticalSkills.map((skill) => (
                                        <div className="dsrRiskItem" key={skill.name}>
                                            <div>
                                                <div className="dsrRiskTitle">{skill.name}</div>
                                                <div className="dsrRiskMeta">{skill.count} evaluations - score moyen {skill.score}</div>
                                            </div>
                                            <span className="dsrBadge dsrBadge--danger">+{skill.gap}</span>
                                        </div>
                                    ))}
                                </div>
                            ) : <div className="dsrEmpty">Aucun écart prioritaire</div>}
                        </div>
                    </div>

                    <div className="dsrPanel">
                        <div className="dsrPanel__title">Forces de l'équipe</div>
                        <div className="dsrPanel__body">
                            {analytics.strongSkills.length ? (
                                <div className="dsrRiskList">
                                    {analytics.strongSkills.map((skill) => (
                                        <div className="dsrRiskItem" key={skill.name}>
                                            <div>
                                                <div className="dsrRiskTitle">{skill.name}</div>
                                                <div className="dsrRiskMeta">{skill.count} evaluations - écart moyen {skill.gap}</div>
                                            </div>
                                            <span className="dsrBadge dsrBadge--success">{skill.score}</span>
                                        </div>
                                    ))}
                                </div>
                            ) : <div className="dsrEmpty">Aucune donnee disponible</div>}
                        </div>
                    </div>
                </div>
            </main>

            {detailsModal === "team" && (
                <DetailsModal
                    title="Collaborateurs de mon équipe"
                    subtitle={`${analytics.teamDetails.length} collaborateur(s) pour la campagne sélectionnée`}
                    onClose={() => setDetailsModal(null)}
                >
                    {analytics.teamDetails.length ? (
                        <div className="dsrTableWrap">
                            <table className="dsrTable">
                                <thead>
                                <tr>
                                    <th>Collaborateur</th>
                                    <th>Poste</th>
                                    <th>Statut</th>
                                    <th>Compétences</th>
                                    <th>Score moyen</th>
                                    <th>Écart moyen</th>
                                </tr>
                                </thead>
                                <tbody>
                                {analytics.teamDetails.map((row, index) => (
                                    <tr key={`${row.collaborator}-${index}`}>
                                        <td>{row.collaborator}</td>
                                        <td>{row.job}</td>
                                        <td>{STATUS_META[row.status]?.label || row.status || "-"}</td>
                                        <td>{row.skillsCount}</td>
                                        <td>{row.score ?? "-"}</td>
                                        <td>{row.gap ?? "-"}</td>
                                    </tr>
                                ))}
                                </tbody>
                            </table>
                        </div>
                    ) : (
                        <div className="dsrEmpty">Aucun collaborateur a afficher.</div>
                    )}
                </DetailsModal>
            )}

            {detailsModal === "to-review" && (
                <DetailsModal
                    title="Collaborateurs à traiter"
                    onClose={() => setDetailsModal(null)}
                >
                    {analytics.toReviewDetails.length ? (
                        <div className="dsrTableWrap">
                            <table className="dsrTable">
                                <thead>
                                <tr>
                                    <th>Collaborateur</th>
                                    <th>Poste</th>
                                    <th>Compétences</th>
                                    <th>Score moyen</th>
                                    <th>Écart moyen</th>
                                    <th>Action attendue</th>
                                </tr>
                                </thead>
                                <tbody>
                                {analytics.toReviewDetails.map((row, index) => (
                                    <tr key={`${row.collaborator}-${index}`}>
                                        <td>{row.collaborator}</td>
                                        <td>{row.job}</td>
                                        <td>{row.skillsCount}</td>
                                        <td>{row.score ?? "-"}</td>
                                        <td>{row.gap ?? "-"}</td>
                                        <td><span className="dsrBadge dsrBadge--warning">Valider</span></td>
                                    </tr>
                                ))}
                                </tbody>
                            </table>
                        </div>
                    ) : (
                        <div className="dsrEmpty">Aucune évaluation en attente de traitement.</div>
                    )}
                </DetailsModal>
            )}

            {detailsModal === "critical-gaps" && (
                <DetailsModal
                    title="Détails des écarts critiques de l'équipe"
                    subtitle={`${analytics.criticalGapDetails.length} compétence(s) avec un écart superieur ou egal a 1 niveau`}
                    onClose={() => setDetailsModal(null)}
                >
                    {analytics.criticalGapDetails.length ? (
                        <div className="dsrTableWrap">
                            <table className="dsrTable">
                                <thead>
                                <tr>
                                    <th>Collaborateur</th>
                                    <th>Poste</th>
                                    <th>Compétence</th>
                                    <th>Domaine</th>
                                    <th>Requis</th>
                                    <th>Obtenu</th>
                                    <th>Écart</th>
                                    <th>Statut</th>
                                </tr>
                                </thead>
                                <tbody>
                                {analytics.criticalGapDetails.map((row, index) => (
                                    <tr key={`${row.collaborator}-${row.skill}-${index}`}>
                                        <td>{row.collaborator}</td>
                                        <td>{row.job}</td>
                                        <td>{row.skill}</td>
                                        <td>{row.domain}</td>
                                        <td>{row.expected}</td>
                                        <td>{row.score}</td>
                                        <td><span className="dsrBadge dsrBadge--danger">+{row.gap}</span></td>
                                        <td>{STATUS_META[row.status]?.label || row.status || "-"}</td>
                                    </tr>
                                ))}
                                </tbody>
                            </table>
                        </div>
                    ) : (
                        <div className="dsrEmpty">Aucun écart critique pour cette équipe.</div>
                    )}
                </DetailsModal>
            )}
        </div>
    );
}
